import React, { useState } from "react";
import { Upload, Typography, message, Button, Input, Radio } from "antd";
import { LeftOutlined, UploadOutlined } from "@ant-design/icons";

const { Dragger } = Upload;
const { Text, Title } = Typography;

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

/**
 * @param {Function} onCancel - go back to the list view
 * @param {Function} onSubmit - async ({ title, base64, filename, isSharedWithPatient? }) => void
 * @param {string} backLabel - e.g. "Back to My Documents"
 * @param {Array} visibilityOptions - e.g. [{label:"Private to Care Team", value:false}, {label:"Shared with Patient", value:true}] — omit for the patient flow
 */
const DocumentUploadForm = ({ onCancel, onSubmit, backLabel = "Back", visibilityOptions }) => {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [isSharedWithPatient, setIsSharedWithPatient] = useState(visibilityOptions?.[0]?.value);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = title.trim() && file && (!visibilityOptions || isSharedWithPatient !== undefined);

  const handleUpload = () => {
    if (!canSubmit) {
      message.error("Please enter a title and choose a file to upload.");
      return;
    }

    setSubmitting(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        await onSubmit({
          title: title.trim(),
          base64,
          filename: file.name,
          ...(visibilityOptions ? { isSharedWithPatient } : {}),
        });
      } finally {
        setSubmitting(false);
      }
    };
    reader.onerror = () => {
      setSubmitting(false);
      message.error("Could not read the selected file.");
    };
  };

  return (
    <div>
      <div
        onClick={onCancel}
        style={{
          color: "#00ADEF",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 20,
        }}
      >
        <LeftOutlined /> <Text strong style={{ color: "#00ADEF" }}>{backLabel}</Text>
      </div>

      <Title level={5} style={{ letterSpacing: 0.5 }}>NEW DOCUMENT DETAILS</Title>

      <div style={{ marginTop: 20, maxWidth: 480 }}>
        <Text strong>
          <span style={{ color: "red" }}>*</span> Title
        </Text>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ marginTop: 8, marginBottom: 24 }}
        />

        <Text strong>
          <span style={{ color: "red" }}>*</span> Upload Document
        </Text>
        <Dragger
          multiple={false}
          showUploadList={false}
          style={{ marginTop: 8, marginBottom: 24 }}
          beforeUpload={(candidate) => {
            if (!ALLOWED_TYPES.includes(candidate.type)) {
              message.error("Only PDF, JPG, and PNG files are allowed.");
              return Upload.LIST_IGNORE;
            }
            setFile(candidate);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">Drag and drop</p>
          <p className="ant-upload-hint">- OR -</p>
          <Button type="primary" style={{ background: "#00ADEF" }}>Browse Files</Button>
        </Dragger>
        {file && <Text style={{ display: "block", marginBottom: 24 }}>Selected file: {file.name}</Text>}

        {visibilityOptions && (
          <>
            <Text strong>
              <span style={{ color: "red" }}>*</span> Visibility
            </Text>
            <Radio.Group
              value={isSharedWithPatient}
              onChange={(e) => setIsSharedWithPatient(e.target.value)}
              style={{ display: "block", marginTop: 8, marginBottom: 24 }}
            >
              {visibilityOptions.map((opt) => (
                <Radio key={opt.value} value={opt.value} style={{ display: "block", marginBottom: 8 }}>
                  {opt.label}
                </Radio>
              ))}
            </Radio.Group>
          </>
        )}

        <Button
          type="primary"
          disabled={!canSubmit}
          loading={submitting}
          onClick={handleUpload}
          style={{ background: canSubmit ? "#00ADEF" : undefined }}
        >
          Upload Document
        </Button>
      </div>
    </div>
  );
};

export default DocumentUploadForm;
